import { useCallback, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { Toaster } from 'sonner';
import { StartupOverlay } from '@/app/start/StartupOverlay';
import { useTheme } from '@/theme/ThemeProvider';
import { CommandPalette } from './command/CommandPalette';
import { useCommandHotkey } from './command/use-command-hotkey';
import { DensityProvider } from './density/DensityProvider';
import { InstallInvite } from '@/pwa/InstallInvite';
import { useAccessVersionPoller } from '@/lib/use-access-version-poller';
import { useNewVersionAvailable } from '@/pwa/new-version';
import { NewVersionNotice } from '@/pwa/NewVersionNotice';
import { FirstLoadNotice } from './FirstLoadNotice';
import { ShellFooter } from './footer/ShellFooter';
import { ShellHeader } from './header/ShellHeader';
import { useNavigationModel } from './model/use-navigation-model';
import { BottomTabBar } from './mobile/BottomTabBar';
import { NavigationDrawer } from './NavigationDrawer';
import { AppRail } from './rail/AppRail';
import { useRailPreference } from './rail/rail-preference';
import { ScopeBar } from './scope/ScopeBar';
import { SectionTabs } from './SectionTabs';

/**
 * «Vai al contenuto» moves the focus into the main landmark itself: a plain `#main` jump would
 * write a hash into the address, which the router owns, and would leave the focus where it was in
 * browsers that only scroll.
 */
function skipToContent(event: MouseEvent<HTMLAnchorElement>) {
  event.preventDefault();
  document.getElementById('main')?.focus();
}

function ShellMain({
  modules,
  active,
  drawerOpen,
  openDrawer,
  openPalette,
  newVersion,
}: {
  readonly modules: ReturnType<typeof useNavigationModel>['modules'];
  readonly active: ReturnType<typeof useNavigationModel>['active'];
  readonly drawerOpen: boolean;
  readonly openDrawer: () => void;
  readonly openPalette: () => void;
  readonly newVersion: boolean;
}) {
  return (
    <div className="app-main">
      <ShellHeader
        active={active}
        modules={modules}
        drawerOpen={drawerOpen}
        onOpenDrawer={openDrawer}
        onOpenCommandPalette={openPalette}
      />
      <ScopeBar />
      <SectionTabs active={active} />
      {/* One check, two bands: the new version wins over the install invite (#418). */}
      <NewVersionNotice available={newVersion} />
      <InstallInvite updateShown={newVersion} />
      <FirstLoadNotice />
      {/* The screen's one main landmark. `.app-main` stays a div: it also holds the header,
          the footer and the bottom bar, which are not the content. */}
      <main id="main" tabIndex={-1} className="shell-content">
        <div className="shell-capped">
          <Outlet />
        </div>
      </main>
      <ShellFooter />
      <BottomTabBar
        modules={modules}
        active={active}
        drawerOpen={drawerOpen}
        onOpenDrawer={openDrawer}
      />
    </div>
  );
}

/**
 * The shell, as a composition root and nothing else.
 *
 * It holds four pieces of state — is the drawer open, is the palette open, is the rail collapsed,
 * and the navigation model — and assembles the components around them. Every decision it used to make itself now
 * lives with the thing that owns it: the entries and their gates in `model/module-catalog.ts`, the
 * width thresholds in `styles/shell.css`, the session controls in `rail/AccountMenu.tsx`. That is
 * the modularity the rebuild was for: changing a module, a threshold, or the account menu means
 * opening one file, and none of them is this one.
 *
 * WHAT THE STRUCTURE IS
 *
 *   .app-shell                     the `shell` container — every chrome threshold is measured here;
 *                                  `data-rail` carries the person's collapse choice
 *     .shell-grid                  the columns those thresholds change
 *       AppRail variant="fixed"    252px → 64px icon rail → out of flow, per that container; the
 *                                  `rail` container inside it dresses whichever width it gets
 *     NavigationDrawer             the same rail, portalled, for when it is out of flow — with the
 *                                  display preferences and the footer, as the phone's "Altro" menu
 *     .app-main                    the content SHEET (Brand Kit 1.0.0 § The frame): the rail sits
 *                                  on the frame, the screens on this rounded surface
 *       ShellHeader                one row: breadcrumb, search trigger, bell, display preferences
 *       ScopeBar                   the organization, at every width
 *       SectionTabs                the active module's sections — only below 900px, where the
 *                                  rail that carries them is out of the flow
 *       FirstLoadNotice            the one-time migration banner
 *       .shell-content
 *         .shell-capped            the `content` container — 1440px cap, priority columns
 *           <Outlet />
 *       ShellFooter                version, environment, service links — from 620px up; a phone
 *                                  keeps it in the drawer instead
 *       BottomTabBar               phone only, per that container: four modules and "Altro"
 *
 * TWO CONTAINERS, NOT ONE, and the reason is in `shell.css`'s header comment: chrome measures the
 * shell, screen content measures the space the screen actually gets. This is the only file that
 * mounts either of them.
 *
 * THE GATES ARE STILL CONVENIENCE ONLY (FR-6.3). Every module route enforces its own entitlement
 * and permission checks server-side — `WorkspaceDomainGate`, `FinanceGate`, `AdminGate`,
 * `ClinicalStudyGate` — because a hidden nav entry leaves the URL reachable by typing it. The
 * catalog decides what is ADVERTISED; the routes decide what is permitted.
 *
 * The rail is keyed on the active organization so it re-evaluates on every context switch (FR-6.4).
 */
export function Shell() {
  const { t } = useTranslation('shell');
  const location = useLocation();
  // The drawer is open only on the location it was opened from (#353). Any navigation, from any
  // link inside it, closes it, including links that know nothing about the drawer, such as the
  // account panel's «Profilo». Derived from the location rather than reset in an effect, so there
  // is no render with the new page under a drawer still open.
  const [drawerOpenedAt, setDrawerOpenedAt] = useState<string | null>(null);
  const drawerOpen = drawerOpenedAt === location.key;
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { resolvedTheme } = useTheme();
  const { modules, auxiliaryScreens, active, organizationId, settled } = useNavigationModel();
  const { rail, toggleRail } = useRailPreference();

  const closeDrawer = useCallback(() => setDrawerOpenedAt(null), []);
  const openDrawer = useCallback(() => setDrawerOpenedAt(location.key), [location.key]);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  // Stable identity: `useCommandHotkey` re-binds its document listener whenever this changes, and
  // an inline arrow would re-bind on every render of the whole shell.
  const openPalette = useCallback(() => setPaletteOpen(true), []);

  useCommandHotkey(openPalette);
  const newVersion = useNewVersionAvailable();
  useAccessVersionPoller();

  return (
    <DensityProvider>
      {/* Two elements, not one: `.app-shell` establishes the `shell` containment context and
          `.shell-grid` carries the columns. An element is never its own container, so a query on
          `shell` cannot restyle the element that declares the name — see `shell.css`'s note. */}
      <div className="app-shell" data-rail={rail}>
        {/* The keyboard's first stop, shown only while it has the focus: past the rail, the
            header, the scope row and the tabs, straight into the screen (WCAG 2.4.1). */}
        <a className="skip-link" href="#main" onClick={skipToContent}>
          {t('skipToContent')}
        </a>
        <div className="shell-grid">
          <AppRail
            variant="fixed"
            modules={modules}
            active={active}
            onToggleRail={toggleRail}
            onOpenDrawer={openDrawer}
            drawerOpen={drawerOpen}
            key={organizationId}
          />

          <NavigationDrawer open={drawerOpen} onClose={closeDrawer}>
            <AppRail
              variant="drawer"
              modules={modules}
              active={active}
              onNavigate={closeDrawer}
              key={organizationId}
            />
          </NavigationDrawer>

          <ShellMain
            modules={modules}
            active={active}
            drawerOpen={drawerOpen}
            openDrawer={openDrawer}
            openPalette={openPalette}
            newVersion={newVersion}
          />
        </div>
      </div>

      {/*
        BOTH OF THESE SIT OUTSIDE `.app-shell`, AND THAT IS LOAD-BEARING.

        `container-type: inline-size` brings `contain: layout` with it, and a layout-contained box
        is the containing block for its `position: fixed` descendants. `.app-shell` is as tall as
        the document, so a `fixed` element inside it anchors to the bottom of the DOCUMENT rather
        than the viewport: sonner's toaster (`[data-sonner-toaster]` is `position: fixed`) would
        scroll away down the page. The undo toast is the platform's ONE deletion rule — there is no
        confirmation dialog anywhere — so it going missing on a long list is not a cosmetic bug.

        The palette portals to `document.body` and would be unaffected wherever it was written, but
        it lives here too so the pair reads as one rule rather than one rule and one exception.
      */}
      <CommandPalette
        open={paletteOpen}
        onClose={closePalette}
        modules={modules}
        auxiliaryScreens={auxiliaryScreens}
        organizationId={organizationId}
      />
      <Toaster theme={resolvedTheme} />
      <StartupOverlay settled={settled} />
    </DensityProvider>
  );
}
